import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X } from 'lucide-react';


import { API_URL } from '../config/api';
const API = API_URL;
const PollPopup = ({ onClose }) => {
  const [polls, setPolls] = useState([]);
  const [currentPoll, setCurrentPoll] = useState(null);
  const [selectedOption, setSelectedOption] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    fetchActivePolls();
  }, []);

  const fetchActivePolls = async () => {
    try {
      const token = localStorage.getItem('lms_token');
      const response = await axios.get(`${API}/polls/active`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data && response.data.length > 0) {
        setPolls(response.data);
        setCurrentPoll(response.data[0]); // Show first poll
      } else {
        // No active polls, close popup
        onClose();
      }
    } catch (error) {
      console.error('Error fetching polls:', error);
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedOption || !currentPoll) return;

    setSubmitting(true);
    try {
      const token = localStorage.getItem('lms_token');
      await axios.post(
        `${API}/polls/respond`,
        {
          poll_id: currentPoll.id,
          response: selectedOption
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setSubmitted(true);
      
      // Auto close after 2 seconds
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      console.error('Error submitting poll:', error);
      alert('Failed to submit response. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4">
          <p className="text-center text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!currentPoll) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 flex items-center justify-between">
          <h3 className="text-white font-bold text-lg">Quick Poll</h3>
          <button
            onClick={onClose}
            className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-1 transition-all"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {submitted ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h4 className="text-xl font-bold text-gray-800 mb-2">Thank you for helping us!</h4>
              <p className="text-gray-600">Your response has been recorded.</p>
            </div>
          ) : (
            <>
              {/* Poll Question */}
              <div className="mb-6">
                <p className="text-gray-700 text-lg font-medium mb-4">
                  {currentPoll.question}
                </p>
              </div>

              {/* Options */}
              <div className="space-y-3 mb-6">
                {currentPoll.poll_type === 'yes_no' && (
                  <>
                    <button
                      onClick={() => setSelectedOption('yes')}
                      className={`w-full py-4 px-6 rounded-xl font-medium transition-all ${
                        selectedOption === 'yes'
                          ? 'bg-blue-600 text-white shadow-lg scale-105'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      Yes
                    </button>
                    <button
                      onClick={() => setSelectedOption('no')}
                      className={`w-full py-4 px-6 rounded-xl font-medium transition-all ${
                        selectedOption === 'no'
                          ? 'bg-blue-600 text-white shadow-lg scale-105'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      No
                    </button>
                  </>
                )}

                {currentPoll.poll_type === 'multiple_choice' && currentPoll.options && (
                  currentPoll.options.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setSelectedOption(option.value)}
                      className={`w-full py-4 px-6 rounded-xl font-medium transition-all ${
                        selectedOption === option.value
                          ? 'bg-blue-600 text-white shadow-lg scale-105'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))
                )}
              </div>

              {/* Submit Button */}
              <button
                onClick={handleSubmit}
                disabled={!selectedOption || submitting}
                className={`w-full py-3 rounded-xl font-medium transition-all ${
                  selectedOption && !submitting
                    ? 'bg-green-600 text-white hover:bg-green-700 shadow-lg'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {submitting ? 'Submitting...' : 'Submit'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PollPopup;
